import { useState, useRef, useEffect } from 'react'
import { toast } from '@/components/toasts/toast.helpers'
import { SocketClient } from '@/utils/classes/SocketClient'

const KEEPALIVE_BYTES = 9
const KEEPALIVE_INTERVAL_MS = 20000

export const useInstantDemoSetup = ({ INITIAL_SETUP_STATE, wsUrl }) => {
  const [setupState, setSetupState] = useState(INITIAL_SETUP_STATE)

  const clientRef = useRef(null)
  const setupStateRef = useRef(null)

  // Keep ref synced to avoid stale closures in WS callbacks
  useEffect(() => {
    setupStateRef.current = setupState
  }, [setupState])

  const disconnect = () => {
    if (clientRef.current?.keepAlive) {
      clearInterval(clientRef.current.keepAlive)
      clientRef.current.keepAlive = null
    }

    clientRef.current?.disconnect()
    clientRef.current = null
  }

  const fail = (message) => {
    setSetupState((prev) => ({
      ...prev,
      error: message,
      isSettingUp: false,
    }))
    toast.error(message)
    disconnect()
  }

  const handleWebSocketMessage = ({ data }) => {
    let msg
    try {
      msg = JSON.parse(data)
    } catch {
      return
    }

    switch (msg.type) {
      case 'keepalive':
        break

      case 'status':
        setSetupState((prev) => ({
          ...prev,
          progress: msg.progress ?? prev.progress,
          message: msg.message ?? prev.message,
        }))
        break

      case 'organization_created':
        setSetupState((prev) => ({
          ...prev,
          progress: msg.progress,
          message: 'Organization created successfully',
          organizationData: msg.data,
        }))
        break

      case 'complete':
        setSetupState((prev) => ({
          ...prev,
          progress: 100,
          isSettingUp: false,
          isComplete: true,
          message: msg.message,
          completionData: {
            organization_id: msg.data?.organization_id,
            call_ids: msg.data?.call_ids ?? [],
            omnichannel: msg.data?.omnichannel ?? null,
          },
        }))
        toast.success(msg.message)
        disconnect()
        break

      case 'error':
        fail(msg.message)
        break

      case 'trend_created':
        setSetupState((prev) => ({
          ...prev,
          progress: msg.progress ?? prev.progress,
          message: msg.message ?? prev.message,
        }))
        toast.success(msg.message)
        break

      case 'trend_creation_failed':
        setSetupState((prev) => ({
          ...prev,
          progress: msg.progress ?? prev.progress,
          message: msg.message ?? prev.message,
        }))
        toast.warning(msg.message)
        break

      default:
        console.warn('Unknown WS message:', msg)
    }
  }

  const connectWebSocket = (payload) => {
    disconnect()

    const client = new SocketClient()
    clientRef.current = client

    setSetupState((prev) => ({
      ...prev,
      connectionStatus: 'connecting',
      message: 'Connecting to server...',
    }))

    client.connect({
      url: wsUrl,
      username: payload.auth_username,
      token: payload.auth_token,
      organizationid: payload.auth_organization_id,
    })

    client.on('open', () => {
      if (!clientRef.current.keepAlive) {
        clientRef.current.keepAlive = setInterval(() => {
          try {
            const ping = new Uint8Array(KEEPALIVE_BYTES)
            clientRef.current?.socket?.send(ping)
          } catch (error) {
            console.warn('WS keepalive send failed:', error)
          }
        }, KEEPALIVE_INTERVAL_MS)
      }

      setSetupState((prev) => ({
        ...prev,
        connectionStatus: 'connected',
        message: 'Connected to server',
      }))

      client.sendJson(payload)

      setSetupState((prev) => ({
        ...prev,
        isSettingUp: true,
        progress: 0,
        message: 'Starting organization setup...',
      }))
    })

    client.on('message', handleWebSocketMessage)

    client.on('close', () => {
      const { isSettingUp, isComplete } = setupStateRef.current

      setSetupState((prev) => ({
        ...prev,
        connectionStatus: 'disconnected',
      }))

      if (isSettingUp && !isComplete) {
        fail('Connection lost during setup')
      }

      disconnect()
    })
  }

  const startSetup = (payload) => {
    disconnect()

    setSetupState((prev) => ({
      ...INITIAL_SETUP_STATE,
      connectionStatus: prev.connectionStatus,
    }))

    connectWebSocket(payload)
  }

  const stopSetup = () => {
    disconnect()
    setSetupState((prev) => ({
      ...prev,
      isSettingUp: false,
      connectionStatus: 'idle',
      message: 'Setup cancelled.',
    }))
  }

  const resetSetup = () => {
    disconnect()
    setSetupState(INITIAL_SETUP_STATE)
  }

  useEffect(() => {
    return () => disconnect()
  }, [])

  return { setupState, startSetup, stopSetup, resetSetup }
}
